import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { RichText } from '@components/RichText';
import { getBlogPostBySlug } from '@lib/firebase/blog';
import { ROUTES } from '@lib/routes';
import { format } from 'date-fns';

/**
 * Individual blog post page
 * Displays a single blog post with rich text content
 */
export function BlogPostPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => {
      if (!slug) throw new Error('Slug is required');
      return getBlogPostBySlug(slug);
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to={ROUTES.BLOG}
            className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
          >
            ← Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Back Link */}
        <Link
          to={ROUTES.BLOG}
          className="inline-flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:underline mb-8 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Blog
        </Link>

        {/* Featured Image */}
        {post.featuredImage && (
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-96 object-cover rounded-lg mb-8"
          />
        )}

        {/* Header */}
        <header className="mb-8">
          {post.category && (
            <span className="inline-block px-4 py-2 text-sm font-semibold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
              {post.category}
            </span>
          )}
          <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400 mb-6">
            {post.publishedAt && (
              <time dateTime={post.publishedAt.toISOString()}>
                {format(post.publishedAt, 'MMMM d, yyyy')}
              </time>
            )}
            {post.authorName && (
              <>
                <span>•</span>
                <span>By {post.authorName}</span>
              </>
            )}
          </div>
          {post.excerpt && (
            <div className="text-xl text-gray-600 dark:text-gray-400 italic prose prose-xl dark:prose-invert max-w-none">
              <RichText content={post.excerpt} />
            </div>
          )}
        </header>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag: string) => (
              <span
                key={tag}
                className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {post.blocks.filter(block => block.visible && block.type === 'richText').map(block => (
            <RichText
              key={block.id}
              content={block.type === 'richText' ? block.content : ''}
              customFonts={post.settings.customFonts}
            />
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to={ROUTES.BLOG}
            className="inline-flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:underline font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
        </footer>
      </article>
    </Layout>
  );
}

